import React, { useReducer, useState } from "react";
import * as _ from "lodash";
import { inject, observer } from "mobx-react";

import { UserStore } from "Stores/index";
import { type User } from "Stores/userStore";
import type { MapType, Mapping, Committee, Course, Student } from "Stores/commonStore";

import { Utils } from "@/common/utils";

import { Button, Offcanvas, Spinner, Stack, Row, Col, Card, Placeholder, Form } from "react-bootstrap";

import CreateUserForm from "./CreateUserForm";
import { UserRole } from "./roles";
import { useAppContext } from "@/contexts/app";
import { useGetStudents } from "@/hooks/students";
import { useGetCommittees } from "@/hooks/committees";
import { useCourses } from "@/hooks/courses";

export type UserRoleValue = (typeof UserRole)[keyof typeof UserRole];

export type UserTypes = { value: UserRoleValue; label: string };

export interface FormDrawerState {
    showModal: boolean;
    isCreatingOrUpdating: boolean;
    firstName: string;
    lastName: string;
    suffix: string;
    prefix: string;
    email: string;
    isActive: boolean;
    groups: string[];
    isAdmin: boolean;
    isKeyAdmin: boolean;
    isKeyAdminNote: string | null;
    isTemporary: boolean;
    removeOn: Date | null;
    user: User | null | undefined;
    committees: Committee[] | undefined;
    courses: Course[] | undefined;
    students: Student[] | undefined;
    hasAdded: boolean;
    hasSentInvite: boolean;
}

export type TField = keyof FormDrawerState;

export type Action = { type: "SET_FIELD"; field: TField; value: any } | { type: "RESET"; payload: FormDrawerState };

const reducer = (state: FormDrawerState, action: Action): FormDrawerState => {
    switch (action.type) {
        case "SET_FIELD":
            return { ...state, [action.field]: action.value };
        case "RESET":
            return action.payload;
        default:
            return state;
    }
};

const getInitialState = (): FormDrawerState => ({
    showModal: true,
    isCreatingOrUpdating: false,
    suffix: "",
    prefix: "",
    firstName: "",
    lastName: "",
    email: "",
    isActive: true,
    groups: [],
    isAdmin: false,
    isKeyAdmin: false,
    isKeyAdminNote: null,
    isTemporary: false,
    removeOn: null,
    hasAdded: false,
    hasSentInvite: false,
    user: null,
    committees: [],
    courses: [],
    students: [],
});

export interface FormDrawerProps {
    userStore?: UserStore;
    // commonStore?: CommonStore;
    // currentUser?: User;
    domainWhitelist?: string | null;
    handleSubmitUser: () => void;
    handleCloseModal: () => void;
}

const CreateUserDrawer = inject("userStore")(
    observer((props: FormDrawerProps) => {
        const { userStore, handleSubmitUser, handleCloseModal } = props;

        const { dispatchError } = useAppContext();
        const { data: students, isLoading: isStudentsLoading } = useGetStudents();
        const { data: committees, isLoading: isLoadingCommittees } = useGetCommittees();
        const { data: courses } = useCourses();

        const isRecordsReady = students && committees && courses;

        // states
        const [isSaving, setIsSaving] = useState(false);
        const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);

        const [state, dispatch] = useReducer(reducer, getInitialState());

        // computed
        const isValid = () => {
            const { firstName, lastName, email } = state;
            return firstName && lastName && email;
        };

        // actions
        const setField = <K extends TField>(field: TField, value: FormDrawerState[K]) => dispatch({ type: "SET_FIELD", field, value });

        const resetState = (): void => {
            setField("hasAdded", true);
            dispatch({ type: "RESET", payload: getInitialState() });
        };

        const handleCancel = () => {
            if (state.hasAdded) {
                handleSubmitUser();
            } else {
                handleCloseModal();
            }
        };

        const handleSaveError = (e) => {
            const errorType = e.response?.data?.result?.error;
            const errorMessage = e.response?.data?.result?.message;

            if (e.response && errorType === "ExhaustedLicenses") {
                dispatchError(e, "You have reached the limit of users for your school. Please contact support@enflux.com to add more.");
            } else if (e.response && errorType === "UserExists") {
                dispatchError(e, errorMessage);
            } else {
                dispatchError(e);
            }
        };

        const saveUser = async (addAnother: boolean): Promise<void> => {
            addAnother ? setIsSavingAndAdding(true) : setIsSaving(true);

            if (!userStore) return;
            // TODO: use the school that has been chosen by user
            //  hunter 2020-04-14: API will need to be updated to allow this. Currently the API uses the
            //   school_id in the api route being called.
            const creator = Utils.getCurrentUser();
            if (!creator) {
                return;
            }

            const removeOn = state.removeOn ? new Date(new Date(state.removeOn as unknown as string).setUTCHours(1, 0, 0, 0)).toISOString() : null;

            const user = {
                suffix: state.suffix,
                prefix: state.prefix,
                first_name: state.firstName,
                last_name: state.lastName,
                email: state.email,
                groups: state.groups,
                status: state.isActive ? "active" : "inactive",
                created_by: creator.email,
                is_admin: state.isAdmin,
                is_key_admin: state.isKeyAdmin,
                remove_on: removeOn,
            };

            try {
                const savedUser = (await userStore.createUser(user)) as User;

                saveMappings(savedUser, addAnother);
            } catch (e) {
                handleSaveError(e);
            } finally {
                setIsSaving(false);
                setIsSavingAndAdding(false);
            }
        };

        const saveMapping = async (user: User, mapping: Mapping, shouldRetry = false): Promise<void> => {
            if (!userStore) return;

            let newMapping: any = [];
            if (state?.[mapping]) {
                newMapping = state[mapping]!.map((obj: MapType) => {
                    if (mapping === "students") {
                        return obj.ruuid;
                    }
                    return obj.id;
                });
            }

            if (newMapping.length === 0) return;

            userStore.addRemoveFacultyMappings(user, mapping, newMapping, [], shouldRetry);
        };

        const saveMappings = (user: User, addAnother: boolean) => {
            Promise.all([saveMapping(user, "courses"), saveMapping(user, "students"), saveMapping(user, "committees", true)])
                .then(() => {
                    if (!addAnother) {
                        handleSubmitUser();
                    } else {
                        resetState();
                    }
                })
                .catch((e) => {
                    handleSaveError(e);
                });
        };

        const getButtonContent = (text: string, isLoading: boolean) => (isLoading ? <Spinner animation="border" variant="secondary" size="sm" /> : text);

        const saveButtonContent = getButtonContent("Save Changes", isSaving);
        const saveAndAddButtonContent = getButtonContent("Save and Create Another", isSavingAndAdding);

        return (
            <Offcanvas id="user-form-drawer" show={true} onHide={handleCloseModal} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Create User</Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    {(!isStudentsLoading || !isLoadingCommittees) && isRecordsReady ? (
                        <CreateUserForm state={state} setField={setField} students={students.data} courses={courses.data} committees={committees.data} {...props} />
                    ) : (
                        <Card className="p-4" data-testid="skeleton-loader">
                            <Placeholder className="skeleton-placeholder" as="h5" animation="wave">
                                <Placeholder className="skeleton-placeholder" xs={3} />
                            </Placeholder>

                            {/* Prefix & First Name */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Placeholder className="skeleton-placeholder" as={Form.Label} animation="wave">
                                        <Placeholder className="skeleton-placeholder" xs={6} />
                                    </Placeholder>
                                    <Placeholder className="w-100 skeleton-placeholder" style={{ height: "48px" }} />
                                </Col>
                                <Col md={6}>
                                    <Placeholder className="skeleton-placeholder" as={Form.Label} animation="wave">
                                        <Placeholder className="skeleton-placeholder" xs={6} />
                                    </Placeholder>
                                    <Placeholder className="w-100 skeleton-placeholder" style={{ height: "48px" }} />
                                </Col>
                            </Row>

                            {/* Last Name & Suffix */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Placeholder className="skeleton-placeholder" as={Form.Label} animation="wave">
                                        <Placeholder className="skeleton-placeholder" xs={6} />
                                    </Placeholder>
                                    <Placeholder className="w-100 skeleton-placeholder" style={{ height: "48px" }} />
                                </Col>
                                <Col md={6}>
                                    <Placeholder className="skeleton-placeholder" as={Form.Label} animation="wave">
                                        <Placeholder className="skeleton-placeholder" xs={6} />
                                    </Placeholder>
                                    <Placeholder className="w-100 skeleton-placeholder" style={{ height: "48px" }} />
                                </Col>
                            </Row>

                            {/* Email Field */}
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Placeholder className="skeleton-placeholder" as={Form.Label} animation="wave">
                                        <Placeholder className="skeleton-placeholder" xs={6} />
                                    </Placeholder>
                                    <Placeholder className="w-100 skeleton-placeholder" style={{ height: "48px" }} />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <div className="form-buttons">
                        <Stack gap={2}>
                            <Button variant="outline-light" id="user-form-saveAdd" disabled={!isValid() || isSavingAndAdding} onClick={() => saveUser(true)}>
                                {saveAndAddButtonContent}
                            </Button>
                            <Button variant="primary" id="user-form-save" disabled={!isValid() || isSaving} onClick={() => saveUser(false)}>
                                {saveButtonContent}
                            </Button>
                            <Button variant="outline-light" id="user-form-cancel" disabled={isSaving} onClick={handleCancel} className="cancel-button">
                                Cancel
                            </Button>
                        </Stack>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        );
    })
);

export default CreateUserDrawer;
